import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { startOfDay, isSameDay, isSameMonth } from 'date-fns';
import { CalendarDateFormatter, CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarMonthViewDay } from 'angular-calendar';

import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { fuseAnimations } from '@fuse/animations';

import { EmployeeCalendarEventModel } from './event.model';
// import { EmployeeCalendarEventFormDialogComponent } from './event-form/event-form.component';
import { CalendarService } from '@fuse/services/calendar.service';

import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';

import { takeUntil } from 'rxjs/operators';
import { CompanyService } from '@fuse/services/company.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateAdapter } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { CustomDateFormatter } from './custom-date-formatter.provider';

const moment = _rollupMoment || _moment;

@Component({
    selector: 'employee-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    providers: [
        {
            provide: CalendarDateFormatter,
            useClass: CustomDateFormatter
        }
    ]
})
export class EmployeeCalendarComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<any>;

    actions: CalendarEventAction[];
    activeDayIsOpen: boolean;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    dialogRef: any;
    events: CalendarEvent[];
    eventsMonthly: CalendarEvent[];
    eventsTeam: CalendarEvent[];
    eventsCompany: CalendarEvent[];
    refresh: Subject<any> = new Subject();
    selectedDay: any;
    view: string;
    viewDate: Date;
    company: any;
    addEventDisabled = false;
    locale = 'en';
    lang = localStorage.getItem('lang') || 'sr';
    categories = [6, 14, 15, 16, 17, 18];

    eventTypes = {};

    constructor(
        private _matDialog: MatDialog,
        private _calendarService: CalendarService,
        private _companyService: CompanyService,
        private _translationLoader: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private dateAdapter: DateAdapter<Date>
    ) {
        // Set the defaults
        this._unsubscribeAll = new Subject();
        this.view = 'month';
        this.viewDate = new Date();
        this.activeDayIsOpen = true;
        this.selectedDay = { date: startOfDay(new Date()) };
        this.company = this._companyService.activeCompany;

        this.actions = [
            // {
            //     label: '<i class="material-icons s-16">edit</i>',
            //     onClick: ({ event }: { event: CalendarEvent }): void => {
            //         this.editEvent('edit', event);
            //     }
            // },
            // {
            //     label: '<i class="material-icons s-16">delete</i>',
            //     onClick: ({ event }: { event: CalendarEvent }): void => {
            //         this.deleteEvent(event);
            //     }
            // }
        ];

        /**
         * Get events from service/server
         */
        // this.setEvents(null);
    }

    private getEventColor(type): any {

        const colors = [{
            type: '',
            primary: '#666666',
            secondary: '#666666'
        }, {
            type: 'appointment',
            primary: '#F44336',
            secondary: '#FFCDD2'
        }, {
            type: 'declared job',
            primary: '#FF9800',
            secondary: '#D1E8FF'
        }, {
            type: 'bonus',
            primary: '#1E90FF',
            secondary: '#D1E8FF'
        }, {
            type: 'sick leave',
            primary: '#673AB7',
            secondary: '#D1C4E9'
        }, {
            type: 'day off',
            primary: '#673AB7',
            secondary: '#D1C4E9'
        }, {
            type: 'annual leave',
            primary: '#673AB7',
            secondary: '#D1C4E9'
        }, {
            type: 'Work_Schedule',
            primary: '#673AB7',
            secondary: '#D1C4E9'
        }];
        let retVal = colors[0];
        colors.forEach(item => {
            if (item.type === type) {
                retVal = item;
            }
        });
        return retVal;
    }

    private getCompanyEvents(date): void {
        this._calendarService.getNonWorkingDays(date, this.company.id);
    }

    private getTeamEvents(date): void {
        this._calendarService.getTeamWorkSchedule(date, this.company.id);
    }

    private checkNonWorkingEvent(events): boolean {
        let retVal = false;
        events.forEach(event => {
            if (event.type === 'Non-Working_Day') {
                retVal = true;
            }
        });
        return retVal;
    }

    private getTranslations(): void {
        console.log('getTranslations');
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getMultipleTranslationCategories(this.lang, this.categories);
                this.dateAdapter.setLocale(this.lang);

                // set calendar locale

                if (this.lang === 'sr') {
                    this.locale = 'sr-Latn';
                } else if (this.lang === 'ua') {
                    this.locale = 'ru-UA';
                } else {
                    this.locale = this.lang;
                }
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        const d = moment().format('YYYY-MM');

        this._calendarService.onMonthlyEventsUpdated
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(events => {
                this.setEvents(events);
            });

        this._calendarService.onNonWorkinEventsUpdated
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(events => {
                this.setNonWorkingDays(events);
            });

        this._calendarService.onTeamEventsUpdated
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(events => {
                this.setTeamEventDays(events);
            });

        this._companyService.onCompanyChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                if (this.company.id !== this._companyService.activeCompany.id ? this._companyService.activeCompany.id : response.id) {
                    this.company = this._companyService.activeCompany;
                    this._calendarService.companyId = this.company.id;
                    this._calendarService.getMonthlyEvents();
                }
            });

        // this._calendarService.getMonthlyEvents(d)
        // .then(response => {
        //     console.log(response);
        // });

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });
        this.getTranslations();

    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set events
     */
    setEvents(events): void {

        this.eventsMonthly = events.data.map(item => {
            // item.actions = this.actions;
            const c = this.getEventColor(item.type);

            return new EmployeeCalendarEventModel({
                actions: this.actions,
                id: item.id,
                start: item.start_datetime,
                end: item.end_datetime,
                title: this._translateService.instant(item.title),
                color: {
                    primary: c.primary,
                    secondary: c.secondary
                },
                isRecurring: item.is_recurring,
                type: item.type,
                frequenceTypeOfRecurring: item.frequence_type_of_recurring,
                endOfRecurring: item.end_of_recurring,
                meta: {
                    location: '',
                    notes: item.type
                }
            });
        });

        this.getCompanyEvents(this._calendarService.calendarLastDate);
    }

    setNonWorkingDays(events): void {
        this.addEventDisabled = false;

        this.eventsCompany = [...this.eventsMonthly, ...events.data.map(item => {

            const actual = moment(this.viewDate).format('YYYY-MM-DD');
            const start = moment(item.start_datetime).format('YYYY-MM-DD');
            const end = moment(item.end_datetime).format('YYYY-MM-DD');
            const check = moment(actual).isBetween(start, end) || actual === start;

            if (!this.addEventDisabled && check) {
                this.addEventDisabled = true;
            }

            const c = this.getEventColor(item.type);

            return new EmployeeCalendarEventModel({
                actions: [],
                id: item.id,
                start: item.start_datetime,
                end: item.end_datetime,
                title: this._translateService.instant(item.title),
                color: {
                    primary: c.primary,
                    secondary: c.secondary
                },
                isRecurring: item.is_recurring,
                type: item.type,
                frequenceTypeOfRecurring: item.frequence_type_of_recurring,
                endOfRecurring: item.end_of_recurring,
                meta: {
                    location: '',
                    notes: item.type
                }
            });
        })];

        this.getTeamEvents(this._calendarService.calendarLastDate);

    }

    setTeamEventDays(events): void {
        this.addEventDisabled = false;

        this.events = [...this.eventsCompany, ...events.data.map(item => {

            const actual = moment(this.viewDate).format('YYYY-MM-DD');
            const start = moment(item.start_datetime).format('YYYY-MM-DD');
            const end = moment(item.end_datetime).format('YYYY-MM-DD');
            const check = moment(actual).isBetween(start, end) || actual === start;

            if (!this.addEventDisabled && check) {
                this.addEventDisabled = true;
            }

            const c = this.getEventColor(item.type);

            return new EmployeeCalendarEventModel({
                actions: [],
                id: item.id,
                start: item.start_datetime,
                end: item.end_datetime,
                title: this._translateService.instant(item.title),
                color: {
                    primary: c.primary,
                    secondary: c.secondary
                },
                isRecurring: item.is_recurring,
                type: item.type,
                frequenceTypeOfRecurring: item.frequence_type_of_recurring,
                endOfRecurring: item.end_of_recurring,
                meta: {
                    location: '',
                    notes: item.type
                }
            });
        })];

    }

    /**
     * Before View Renderer
     *
     * @param {any} header
     * @param {any} body
     */
    beforeMonthViewRender({ header, body }): void {
        /**
         * Get the selected day
         */
        const _selectedDay = body.find((_day) => {
            return _day.date.getTime() === this.selectedDay.date.getTime();
        });

        if (_selectedDay) {
            /**
             * Set selected day style
             * @type {string}
             */
            _selectedDay.cssClass = 'cal-selected';
        }

    }

    changeMonth(event): void {
        const d = moment(event.date).format('YYYY-MM');
        this._calendarService.getMonthlyEvents(d);
    }

    /**
     * Day clicked
     *
     * @param {MonthViewDay} day
     */
    dayClicked(day: CalendarMonthViewDay): void {
        const date: Date = day.date;
        const events: CalendarEvent[] = day.events;

        this.addEventDisabled = this.checkNonWorkingEvent(events);

        if (isSameMonth(date, this.viewDate)) {
            if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) || events.length === 0) {
                this.activeDayIsOpen = false;
            }
            else {
                this.activeDayIsOpen = true;
                this.viewDate = date;
            }
        }
        this.selectedDay = day;
        this.refresh.next();
    }

    /**
     * Event times changed
     * Event dropped or resized
     *
     * @param {CalendarEvent} event
     * @param {Date} newStart
     * @param {Date} newEnd
     */
    eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
        event.start = newStart;
        event.end = newEnd;
        // console.warn('Dropped or resized', event);
        this.refresh.next(true);
    }


}


